import React from 'react';
import { RotateCcw, Filter, Archive, Trash2, Mail, CheckCircle2, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import MailItem from '../MailItem/index';

const MailList = ({
    activeFolder,
    filteredEmails,
    selectedEmails,
    handleRefresh,
    selectAll,
    handleAction,
    onEmailClick,
    toggleSelect,
    toggleRead,
    toggleStar,
    onEmailContextMenu,
    isLoading,
    isSyncing,
    loadedFolder,
    error
}) => {
    return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#fff' }}>
            {/* Action Bar */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 16px',
                borderBottom: '1px solid #f1f3f4',
                background: '#fff',
                minHeight: '48px'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', padding: '6px' }}>
                        <input
                            type="checkbox"
                            checked={selectedEmails && filteredEmails && selectedEmails.length > 0 && selectedEmails.length === filteredEmails.length}
                            onChange={selectAll}
                            style={{
                                cursor: 'pointer',
                                width: '16px',
                                height: '16px',
                                accentColor: 'var(--primary)'
                            }}
                        />
                    </div>

                    <motion.div
                        animate={isSyncing ? { rotate: 360 } : { rotate: 0 }}
                        transition={isSyncing ? { duration: 1, repeat: Infinity, ease: "linear" } : { duration: 0.2 }}
                        style={{ cursor: 'pointer', color: '#5f6368', padding: '8px', borderRadius: '50%' }}
                        onClick={handleRefresh}
                        className="btn-hover-light"
                        title={isSyncing ? "Syncing..." : "Refresh"}
                    >
                        <RotateCcw size={18} />
                    </motion.div>

                    {selectedEmails.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', borderLeft: '1px solid #dadce0', paddingLeft: '20px' }}>
                            {activeFolder === 'Trash' ? (
                                <button onClick={() => handleAction('Restore')} className="btn-ghost" title="Restore" style={{ padding: '8px' }}>
                                    <RotateCcw size={18} />
                                </button>
                            ) : activeFolder === 'Archive' ? (
                                <button onClick={() => handleAction('Undo')} className="btn-ghost" title="Undo" style={{ padding: '8px' }}>
                                    <RotateCcw size={18} />
                                </button>
                            ) : (
                                <button onClick={() => handleAction('Archive')} className="btn-ghost" title="Archive" style={{ padding: '8px' }}>
                                    <Archive size={18} />
                                </button>
                            )}
                            <button onClick={() => handleAction('Trash')} className="btn-ghost" title="Delete" style={{ padding: '8px' }}>
                                <Trash2 size={18} />
                            </button>
                        </div>
                    )}
                </div>

                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ fontSize: '0.75rem', color: '#5f6368', fontWeight: '500' }}>
                        {filteredEmails.length > 0 ? `1-${filteredEmails.length} of ${filteredEmails.length}` : '0-0 of 0'}
                    </div>
                </div>
            </div>

            {/* List Header (Subtle) */}
            {selectedEmails.length > 0 && (
                <div style={{ padding: '8px 16px', background: '#f8f9fa', borderBottom: '1px solid #f1f3f4', fontSize: '0.75rem', color: '#5f6368', fontWeight: '600' }}>
                    {selectedEmails.length} selected
                </div>
            )}

            <div className="scrollbar-hide" style={{ flex: 1, overflowY: 'auto', minHeight: 0, position: 'relative' }}>
                <AnimatePresence mode="wait">
                    {(isLoading && filteredEmails.length === 0) ? (
                        <motion.div
                            key="loading-state"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                padding: '100px 20px',
                                background: 'white'
                            }}
                        >
                            <div className="shimmer" style={{ width: '80%', height: '40px', marginBottom: '12px', borderRadius: '8px' }} />
                            <div className="shimmer" style={{ width: '75%', height: '40px', marginBottom: '12px', borderRadius: '8px' }} />
                            <div className="shimmer" style={{ width: '85%', height: '40px', marginBottom: '12px', borderRadius: '8px' }} />
                            <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', marginTop: '20px', fontWeight: '500' }}>
                                Loading your {activeFolder.startsWith('Shared/') ? activeFolder.substring(7) : activeFolder}...
                            </p>
                        </motion.div>
                    ) : error ? (
                        <motion.div
                            key="error-state"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            style={{ padding: '60px 20px', textAlign: 'center' }}
                        >
                            <div style={{ color: 'var(--error)', marginBottom: '16px' }}>
                                <AlertCircle size={48} style={{ margin: '0 auto' }} />
                            </div>
                            <h3 style={{ fontWeight: '700', color: 'var(--text-main)', marginBottom: '8px' }}>Synchronization Needed</h3>
                            <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', marginBottom: '24px', maxWidth: '300px', margin: '0 auto 24px' }}>
                                {error}
                            </p>
                            <button onClick={() => handleRefresh()} className="btn-primary" style={{ margin: '0 auto' }}>Retry Sync</button>
                        </motion.div>
                    ) : filteredEmails && filteredEmails.length > 0 ? (
                        <motion.div 
                            key="email-list"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                opacity: isLoading ? 0.5 : 1,
                                filter: isSyncing && filteredEmails.length > 0 ? 'grayscale(0.2)' : 'none',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            {filteredEmails.map((email) => (
                                <MailItem
                                    key={email.id}
                                    {...email}
                                    isSelected={selectedEmails && selectedEmails.includes(email.id)}
                                    onToggleSelect={toggleSelect}
                                    onToggleRead={toggleRead}
                                    onToggleStar={toggleStar}
                                    onClick={() => onEmailClick(email.id)}
                                    onAction={handleAction}
                                    onContextMenu={(e) => onEmailContextMenu && onEmailContextMenu(e, email)}
                                />
                            ))}
                            {isSyncing && (
                                <div style={{ 
                                    padding: '12px', 
                                    textAlign: 'center', 
                                    fontSize: '0.8rem', 
                                    color: 'var(--primary)', 
                                    background: 'var(--primary-light)',
                                    fontWeight: '600'
                                }}>
                                    Checking for new messages...
                                </div>
                            )}
                        </motion.div>
                    ) : (isLoading || isSyncing) ? (
                         <motion.div
                            key="loading-state-placeholder"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                padding: '100px 20px',
                                background: 'white'
                            }}
                        >
                            <div className="shimmer" style={{ width: '80%', height: '40px', marginBottom: '12px', borderRadius: '8px' }} />
                            <div className="shimmer" style={{ width: '75%', height: '40px', marginBottom: '12px', borderRadius: '8px' }} />
                            <div className="shimmer" style={{ width: '85%', height: '40px', marginBottom: '12px', borderRadius: '8px' }} />
                            <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', marginTop: '20px', fontWeight: '500' }}>
                                Updating {activeFolder.startsWith('Shared/') ? activeFolder.substring(7) : activeFolder}...
                            </p>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="empty-state"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                            style={{ 
                                padding: '120px 40px', 
                                textAlign: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                            }}
                        >
                            <div style={{ 
                                width: '80px', 
                                height: '80px', 
                                borderRadius: '50%', 
                                background: 'var(--bg-secondary)', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                marginBottom: '20px',
                                color: 'var(--text-dim)'
                            }}>
                                <Mail size={40} opacity={0.3} />
                            </div>
                            <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: 'var(--text-main)', marginBottom: '8px' }}>
                                Folder is empty
                            </h3>
                            <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', maxWidth: '240px' }}>
                                No emails found in {activeFolder.startsWith('Shared/') ? activeFolder.substring(7) : activeFolder}.
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                .btn-hover-light:hover { background-color: #f1f3f4; }
                @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
                .shimmer { background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; }
            `}</style>
        </div>
    );
};

export default MailList;
